import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  getPersonasFromQAs,
  getQAsByPersonaId,
  landing_qas,
  Persona,
  QA,
} from "@/lib/constants";
import { useEffect, useState } from "react";

import { PersonaCard } from "@/components/blocks/persona-card";
import { QAItem } from "@/components/blocks/qa-item";

export function LandingEvalDemo() {
  const positiveAspects = [
    {
      category: "Navigation",
      description:
        "Clear organization into sections like Shop, Make, and Learn makes navigation intuitive",
    },
    {
      category: "Visual Design",
      description:
        "Appealing spring-themed imagery with seasonal colors creates an inviting atmosphere",
    },
  ];

  const improvementAreas = [
    {
      category: "Information Density",
      description:
        "Too much content presented at once, creating visual overwhelm for users",
    },
    {
      category: "Call-to-Action",
      description:
        "CTAs lack prominence and specific calls to action, reducing conversion potential",
    },
  ];

  const keyPoints = [
    {
      label: "Clear Organization",
      description: "Well organized into sections like Shop, Make, and Learn",
      type: "primary",
    },
    {
      label: "Spring-Themed Imagery",
      description: "Seasonal imagery featuring the Vanilla Lavender theme",
      type: "primary",
    },
    {
      label: "Product Categorization",
      description: "Categorization by format, need, and caffeine level",
      type: "primary",
    },
    {
      label: "Information Overload",
      description: "Too much content presented at once, overwhelming users",
      type: "secondary",
    },
    {
      label: "CTA Prominence",
      description: "Lack of compelling and specific calls to action",
      type: "secondary",
    },
  ];

  const ctaAnalysis = [
    {
      category: "Visibility and Wording",
      description:
        'The main CTA "Discover Now" is present, but it lacks specificity. A more direct and benefit-driven CTA could be more compelling.',
    },
    {
      category: "Competing CTAs",
      description:
        'There are multiple CTAs like "Try Recipe" and "Shop the Season," which could overwhelm users. Simplifying and prioritizing these could make the page more actionable.',
    },
  ];

  const [qaData, setQaData] = useState<QA[]>([]);
  const [personas, setPersonas] = useState<Persona[]>([]);
  const [selectedPersonaId, setSelectedPersonaId] = useState<number | null>(
    null
  );
  const [filteredQAs, setFilteredQAs] = useState<QA[]>([]);

  const handlePersonaClick = (personaId: number) => {
    setSelectedPersonaId(personaId);
    setFilteredQAs(getQAsByPersonaId(qaData, personaId));
  };

  useEffect(() => {
    // In a real app, you would fetch this data from an API
    // For this example, we'll use the data from the snippet
    const fetchData = async () => {
      try {
        // This would be a fetch call in a real app

        setQaData(landing_qas);
        const extractedPersonas = getPersonasFromQAs(landing_qas);
        setPersonas(extractedPersonas);

        if (extractedPersonas.length > 0) {
          setSelectedPersonaId(extractedPersonas[0].id);
          setFilteredQAs(
            getQAsByPersonaId(landing_qas, extractedPersonas[0].id)
          );
        }

        // setIsLoading(false);
      } catch (error) {
        console.error("Error fetching data:", error);
        // setIsLoading(false);
      }
    };

    fetchData();
  }, []);

  return (
    <div className="bg-slate-50 min-h-screen text-slate-900">
      <div className="max-w-6xl mx-auto p-4 md:p-6">
        {/* Header */}
        {/* <header className="flex justify-between items-center py-4 mb-6 border-b border-slate-200">
          <div className="text-2xl font-bold text-purple-600">consumr.ai</div>
          <div className="flex gap-4">
            <button className="bg-transparent border-none cursor-pointer text-slate-500 hover:text-slate-800 hover:bg-slate-100 w-10 h-10 rounded-full flex items-center justify-center transition-all">
              <Bell size={18} />
            </button>
            <button className="bg-transparent border-none cursor-pointer text-slate-500 hover:text-slate-800 hover:bg-slate-100 w-10 h-10 rounded-full flex items-center justify-center transition-all">
              <User size={18} />
            </button>
          </div>
        </header> */}

        {/* Page Title */}
        <h1 className="text-2xl font-semibold mb-6">
          Landing Page Creative Evaluation Report by Consumr.ai
        </h1>
        <div className="inline-block bg-slate-100 rounded-full px-4 py-2 text-sm mb-4">
          starbucks.com
        </div>

        {/* Preview Container */}
        <div className="relative bg-black rounded-lg  mb-6 h-96 flex items-center justify-center overflow-scroll ">
          <img
            src="https://imagedelivery.net/eqKNURtNCms0uspDZDkHSQ/f3457fd0-c977-466b-a98e-57d7034b5a00/public"
            alt="Starbucks Landing Page"
            className=" w-full  h-full object-cover"
            style={{
              width: "273px",
              height: "768px",
            }}
          />
        </div>

        {/* Score Cards */}
        <div className="grid md:grid-cols-3 gap-4 mb-6">
          <Card className="transition-all hover:translate-y-[-5px]">
            <CardContent className="p-6 text-center">
              <p className="text-sm text-slate-500 mb-2">
                Audience Resonance Score
              </p>
              <div className="text-2xl font-bold flex items-center justify-center">
                7<span className="text-sm text-slate-500 ml-1">/10</span>
              </div>
            </CardContent>
          </Card>

          <Card className="transition-all hover:translate-y-[-5px]">
            <CardContent className="p-6 text-center">
              <p className="text-sm text-slate-500 mb-2">Visual Appeal Score</p>
              <div className="text-2xl font-bold flex items-center justify-center">
                8<span className="text-sm text-slate-500 ml-1">/10</span>
              </div>
            </CardContent>
          </Card>

          <Card className="transition-all hover:translate-y-[-5px]">
            <CardContent className="p-6 text-center">
              <p className="text-sm text-slate-500 mb-2">Overall Score</p>
              <div className="text-2xl font-bold flex items-center justify-center">
                7<span className="text-sm text-slate-500 ml-1">/10</span>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Conclusion Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle className="text-lg font-semibold">Conclusion</CardTitle>
          </CardHeader>
          <CardContent>
            <p className="mb-4">
              The landing page has a well-organized structure with clear
              sections like Shop, Make, and Learn, and features appealing
              seasonal imagery. However, it can be overwhelming due to excessive
              information, and the CTAs could be more specific and prominent to
              improve user engagement.
            </p>

            <Accordion type="single" collapsible>
              <AccordionItem value="rationale">
                <AccordionTrigger>
                  See Rationale of the conclusion
                </AccordionTrigger>
                <AccordionContent>
                  <div className="grid md:grid-cols-2 gap-6 mt-4">
                    <div>
                      <h3 className="font-semibold mb-3">Positive Aspects</h3>
                      {positiveAspects.map((aspect, index) => (
                        <div
                          key={index}
                          className="bg-slate-100 rounded-lg p-3 mb-3"
                        >
                          <div className="inline-block bg-purple-400 text-white px-2 py-1 rounded text-xs mb-2">
                            {aspect.category}
                          </div>
                          <p>{aspect.description}</p>
                        </div>
                      ))}
                    </div>
                    <div>
                      <h3 className="font-semibold mb-3">
                        Areas for Improvement
                      </h3>
                      {improvementAreas.map((area, index) => (
                        <div
                          key={index}
                          className="bg-slate-100 rounded-lg p-3 mb-3"
                        >
                          <div className="inline-block bg-purple-400 text-white px-2 py-1 rounded text-xs mb-2">
                            {area.category}
                          </div>
                          <p>{area.description}</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </AccordionContent>
              </AccordionItem>
            </Accordion>
          </CardContent>
        </Card>

        {/*  */}
        <div className="container mx-auto py-8 px-4">
          <h3 className="text-xl font-bold mb-8 text-center">
            AI Twin Responses
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {personas.map((persona) => (
              <PersonaCard
                key={persona.id}
                persona={persona}
                isSelected={selectedPersonaId === persona.id}
                onClick={() => handlePersonaClick(persona.id)}
              />
            ))}
          </div>

          {selectedPersonaId && (
            <div className="mt-8">
              <h2 className="text-xl font-semibold mb-4">
                Responses History -{""}{" "}
                {personas.find((p) => p.id === selectedPersonaId)?.name}
              </h2>

              {filteredQAs.length > 0 ? (
                <div>
                  {filteredQAs.map((qa, index) => (
                    <QAItem key={index} qa={qa} />
                  ))}
                </div>
              ) : (
                <p className="text-center text-gray-500">
                  No Q&A history available for this persona.
                </p>
              )}
            </div>
          )}
        </div>
        {/*  */}

        {/* Quick Responses Card */}
        <Card className="mb-6">
          <Accordion type="single" collapsible>
            <AccordionItem value="quick-responses">
              <CardHeader className="px-6 py-4 pb-0">
                <AccordionTrigger className="py-2">
                  <CardTitle className="text-lg font-semibold">
                    Quick Responses
                  </CardTitle>
                </AccordionTrigger>
              </CardHeader>
              <AccordionContent>
                <CardContent>
                  {/* Response 1 */}
                  <div className="bg-slate-100 rounded-lg p-4 mb-4">
                    <div className="font-semibold mb-2">Point #1:</div>
                    <div className="text-purple-600 mb-2">
                      What do you like and not like about this landing page
                      design/layout/copy. Give specific points. Remember this a
                      screenshot of Starbucks landing page.
                    </div>
                    <p className="mb-4">
                      The Starbucks landing page impresses with its clear
                      navigation and appealing spring-themed imagery but feels
                      overwhelming due to excessive information and lacks
                      prominent CTAs.
                    </p>

                    <h3 className="font-semibold mb-3">Key Points</h3>
                    <table className="w-full border-collapse">
                      <tbody>
                        {keyPoints.map((point, index) => (
                          <tr key={index} className="border-b border-slate-200">
                            <td className="py-2 pr-4">
                              <Badge
                                variant={
                                  point.type === "primary"
                                    ? "default"
                                    : "secondary"
                                }
                                className={
                                  point.type === "primary"
                                    ? "bg-purple-500"
                                    : ""
                                }
                              >
                                {point.label}
                              </Badge>
                            </td>
                            <td className="py-2">{point.description}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Response 2 */}
                  <div className="bg-slate-100 rounded-lg p-4 mb-4">
                    <div className="font-semibold mb-2">Point #2:</div>
                    <div className="text-purple-600 mb-2">
                      Is there a clear CTA? Do you like how it is
                      displayed/worded? How likely are you to take a next step
                      from this landing page?
                    </div>
                    <p className="mb-4">
                      The CTAs on the Starbucks landing page are visible but
                      could be more specific and engaging. Enhancing the CTA
                      with more action-oriented language and prominence could
                      increase the likelihood of user engagement.
                    </p>

                    <h3 className="font-semibold mb-3">CTA Analysis</h3>
                    {ctaAnalysis.map((item, index) => (
                      <div
                        key={index}
                        className="bg-white rounded-lg p-3 mb-3 shadow-sm"
                      >
                        <div className="inline-block bg-purple-400 text-white px-2 py-1 rounded text-xs mb-2">
                          {item.category}
                        </div>
                        <p>{item.description}</p>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card>

        {/* Review Minutes Card */}
        {/* <Card className="mb-6">
          <Accordion type="single" collapsible>
            <AccordionItem value="review-minutes">
              <CardHeader className="px-6 py-4 pb-0">
                <AccordionTrigger className="py-2">
                  <CardTitle className="text-lg font-semibold">
                    Review the minutes
                  </CardTitle>
                </AccordionTrigger>
              </CardHeader>
              <AccordionContent>
                <CardContent>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
                    {twins.map((twin) => (
                      <div
                        key={twin.id}
                        className="flex flex-col items-center bg-white rounded-lg p-4 shadow-sm cursor-pointer transition-all hover:translate-y-[-5px] hover:shadow-md"
                      >
                        <div className="w-16 h-16 rounded-full bg-slate-200 mb-3 overflow-hidden">
                          <img
                            src={twin.avatar}
                            alt={`${twin.name} avatar`}
                            className="w-full h-full object-cover"
                          />
                        </div>
                        <div className="font-semibold text-center mb-1">
                          {twin.name}
                        </div>
                        <div className="text-xs text-slate-500 text-center">
                          Stage: {twin.stage}
                        </div>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </Card> */}

        {/* Action Buttons */}
        {/* <div className="flex justify-end gap-3 mt-6">
          <Button variant="outline">Download Report</Button>
          <Button>Share Report</Button>
        </div> */}
      </div>
    </div>
  );
}
